import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authApi, errorCode, isNewPasswordChallenge, type AuthTokens, type SignInResult } from '@/api/authApi'
import { userActivityApi } from '@/api/userActivityApi'
import { usersApi } from '@/api/usersApi'
import {
  clearIdleClock,
  resumeIdleClock,
  setSessionEndedReason,
  startIdleClock,
  takeSessionEndedReason,
} from '@/lib/idleActivity'
import { startSso } from '@/lib/sso'
import { topWindow } from '@/lib/topWindow'
import {
  clearAuth,
  getEmail,
  getSession,
  isAuthenticated as hasToken,
  setAuth,
  setSession,
  type AuthSession,
} from '@/lib/authToken'

export type LoginResult =
  | { status: 'ok' }
  | { status: 'new_password_required'; email: string; session: string }
  /** The account is SSO-only; the browser is already navigating to the IdP. */
  | { status: 'sso' }

interface AuthContextValue {
  isAuthenticated: boolean
  email: string | null
  /** The resolved principal (baseline + per-tenant permissions), or null when unknown. */
  user: AuthSession | null
  login: (email: string, password: string) => Promise<LoginResult>
  /** Finish a first-login NEW_PASSWORD_REQUIRED flow, then sign in. */
  completeNewPassword: (email: string, newPassword: string, session: string) => Promise<void>
  /** Finish an SSO login from `/auth/callback`; resolves to the backend's `next`. */
  completeSso: (code: string, state: string) => Promise<string | undefined>
  logout: () => void
  /**
   * True once a session has ended in this page, until the next sign-in. The
   * route guards read it: a page left by signing out is not a destination, so
   * it must not be carried to the login screen as `from` for whoever signs in
   * next.
   */
  sessionEnded: boolean
  /** Re-fetch `/auth/me` (e.g. after editing your own profile). */
  refresh: () => Promise<void>
}

// NOTE: no permission helpers here on purpose. A permission is only meaningful
// relative to a tenant, and this provider WRAPS TenantProvider, so it cannot
// read the selection. Use `usePermission()` from '@/lib/permissions' instead —
// it sits below both providers and is reactive to a tenant switch.
const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [authed, setAuthed] = useState<boolean>(hasToken())
  const [user, setUser] = useState<AuthSession | null>(getSession())
  const [sessionEnded, setSessionEnded] = useState(false)

  const applySession = useCallback((session: AuthSession) => {
    setSession(session)
    setUser(session)
  }, [])

  // After login (or on mount with a live token), pull the resolved principal so
  // roles/permissions are always current.
  useEffect(() => {
    if (!authed) return
    let cancelled = false
    authApi
      .me()
      .then((session) => {
        if (!cancelled) applySession(session)
      })
      .catch(() => {
        // 401s are handled by handleUnauthorized (clears + redirects); ignore here.
      })
    return () => {
      cancelled = true
    }
  }, [authed, applySession])

  const finalize = useCallback(
    async (tokens: AuthTokens, email: string) => {
      // Clear any cached principal before swapping tokens so permission checks
      // never run against a previous user's roles between the token swap and
      // the new /auth/me resolution.
      clearAuth()
      setUser(null)
      // And the SERVER-STATE cache with it. Signing out is SPA navigation, so
      // no page load intervenes and the QueryClient (module scope) outlives the
      // session — every key is user-agnostic (['tenants'], ['patients'], …), so
      // the next principal would be served the previous one's rows straight from
      // cache, and `staleTime` of 5 min means no refetch corrects it. That is a
      // cross-user data leak, not just a stale switcher: it reached the tenant
      // list, clinical lists and everything else cached.
      //
      // Cleared here as well as in logout() because this is the one place every
      // session establishment passes through, including flows that never call
      // logout (first-login password change, or signing in on a tab whose
      // session simply expired).
      queryClient.clear()
      setAuth({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        email,
      })
      // Resolve the new principal BEFORE marking the session authed. If /auth/me
      // fails the login fails — we never want to render the app authed with no
      // resolved roles.
      const session = await authApi.me()
      applySession(session)
      // A sign-in always gets a full idle window, and it has to be started here
      // rather than left to the effect below: signing in on a tab whose session
      // had already lapsed leaves `authed` true throughout, so the effect never
      // re-runs, and a stale timestamp from the old session would end the new
      // one on its first evaluation.
      startIdleClock()
      setSessionEnded(false)
      setAuthed(true)
    },
    [applySession, queryClient],
  )

  // `identifier` may be an email or a username — resolved to the login email first.
  const login = useCallback(
    async (identifier: string, password: string): Promise<LoginResult> => {
      const { email } = await usersApi.resolveLogin(identifier)
      let result: SignInResult
      try {
        result = await authApi.signin(email, password)
      } catch (err) {
        if (errorCode(err) !== 'USE_SSO') throw err
        startSso({ email })
        return { status: 'sso' }
      }
      if (isNewPasswordChallenge(result)) {
        return { status: 'new_password_required', email: result.email, session: result.session }
      }
      await finalize(result, email)
      return { status: 'ok' }
    },
    [finalize],
  )

  const completeNewPassword = useCallback(
    async (email: string, newPassword: string, session: string) => {
      const tokens = await authApi.completeNewPassword(email, newPassword, session)
      await finalize(tokens, email)
    },
    [finalize],
  )

  const completeSso = useCallback(
    async (code: string, state: string) => {
      const { next, ...tokens } = await authApi.ssoComplete(code, state)
      // No email in the response; /auth/me supplies it.
      await finalize(tokens, '')
      return next
    },
    [finalize],
  )

  const logout = useCallback(() => {
    // Recorded for the User audit trail. First: it reads this session's token.
    userActivityApi.logout()
    const sso = getSession()?.sso
    clearAuth()
    setUser(null)
    // Set in the same render as `authed`, so no guard ever sees the signed-out
    // state without it.
    setSessionEnded(true)
    setAuthed(false)
    // Do not leave the signed-out session's server state sitting in memory: it
    // includes patient and submission data, and the next sign-in in this tab
    // reuses this QueryClient. finalize() clears it too; this closes the window
    // between signing out and whatever happens next.
    queryClient.clear()
    // End the Cognito session too, or /sso signs the same user back in through
    // it. The hospital IdP's own session is not ended here. Callers' SPA
    // navigation to /login is the fallback.
    if (sso) {
      // Held across the round-trip so /login still shows why the session ended.
      const reason = takeSessionEndedReason()
      authApi
        .ssoConfig()
        .then(({ logoutUrl }) => {
          if (!logoutUrl) return
          if (reason) setSessionEndedReason(reason)
          // The whole page, also from inside the iPad stage's frame.
          topWindow().location.assign(logoutUrl)
        })
        .catch(() => {})
    }
  }, [queryClient])

  const refresh = useCallback(async () => {
    applySession(await authApi.me())
  }, [applySession])

  // The idle clock runs while signed in, and only while signed in.
  //
  // On load this RESUMES the stored clock rather than restarting it. Restarting
  // here — which is what this did at first — overwrote the last-activity time
  // on every page open, so closing the browser and coming back a week later
  // was treated as a fresh start and the timeout only applied to a tab left
  // open. The clock is started on an actual sign-in, in `finalize`.
  //
  // This REPLACES an auto-logout that fired at the access token's `exp`. That
  // timer signed active users out regardless of what they were doing, because
  // nothing renewed the token; `authFetch` now renews on a 401, so token expiry
  // is no longer the end of a session and inactivity is.
  useEffect(() => {
    if (!authed) {
      clearIdleClock()
      return
    }
    resumeIdleClock()
  }, [authed])

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: authed,
      email: user?.email ?? getEmail(),
      user,
      login,
      completeNewPassword,
      completeSso,
      logout,
      sessionEnded,
      refresh,
    }),
    [authed, user, login, completeNewPassword, completeSso, logout, sessionEnded, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** The auth context, or undefined outside an AuthProvider. See useOptionalTenant. */
export function useOptionalAuth(): AuthContextValue | undefined {
  return useContext(AuthContext)
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
